import React from 'react';
import { MdCheckCircle, MdErrorOutline, MdRadioButtonUnchecked } from 'react-icons/md';

export type AnalysisStage = 'idle' | 'loading-reports' | 'applying-rules' | 'consolidating' | 'done' | 'error';

const STAGE_ORDER: AnalysisStage[] = ['loading-reports', 'applying-rules', 'consolidating'];

const STAGE_LABELS: Record<string, string> = {
    'loading-reports': 'Carregando documentos do lote',
    'applying-rules': 'Aplicando regras',
    consolidating: 'Consolidando resultados',
};

export interface AnalysisProgressState {
    stage: AnalysisStage;
    /** Documentos já carregados do data-provider. */
    reportsLoaded: number;
    /** Total esperado de documentos, quando conhecido. */
    reportsTotal: number | null;
    /** Documentos com recomendação já aplicada. */
    analyzed: number;
    /** Provider sendo processado (relevante na tela do parecer, que carrega vários). */
    currentProvider?: string;
    errorMessage?: string;
}

export const initialAnalysisProgress: AnalysisProgressState = {
    stage: 'idle',
    reportsLoaded: 0,
    reportsTotal: null,
    analyzed: 0,
};

const StageRow: React.FC<{
    stage: AnalysisStage;
    current: AnalysisStage;
    detail?: string;
}> = ({ stage, current, detail }) => {
    const currentIndex = STAGE_ORDER.indexOf(current);
    const stageIndex = STAGE_ORDER.indexOf(stage);
    const isDone = current === 'done' || (currentIndex > -1 && stageIndex < currentIndex);
    const isActive = stage === current;

    let icon = <MdRadioButtonUnchecked className="text-gray-300 dark:text-slate-600" size={18} />;
    if (isDone) {
        icon = <MdCheckCircle className="text-green-500" size={18} />;
    } else if (isActive) {
        icon = (
            <span
                className="block h-[18px] w-[18px] animate-spin rounded-full border-2 border-blue-500 border-t-transparent"
                aria-hidden="true"
            />
        );
    }

    return (
        <li className="flex items-center gap-3">
            <span className="flex h-[18px] w-[18px] shrink-0 items-center justify-center">{icon}</span>
            <span
                className={`text-sm ${
                    isActive
                        ? 'font-semibold text-gray-900 dark:text-gray-100'
                        : isDone
                          ? 'text-gray-600 dark:text-gray-400'
                          : 'text-gray-400 dark:text-slate-500'
                }`}
            >
                {STAGE_LABELS[stage]}
            </span>
            {detail && (
                <span className="ml-auto font-mono text-xs text-gray-500 dark:text-gray-400">{detail}</span>
            )}
        </li>
    );
};

/**
 * AF-3418: substitui a barra de progresso azul com spinner que ficava num overlay
 * bloqueando a tela inteira.
 *
 * Mostra em que etapa a análise está e o contador de cada uma. As medições em produção
 * mostraram que o tempo se concentra no carregamento dos documentos (paginação do
 * data-provider), não na aplicação das regras — então separar as etapas informa de fato
 * onde o usuário está esperando, em vez de uma porcentagem única sem significado.
 *
 * Fica embutido na página (não em overlay) para a tabela poder ser preenchida em paralelo.
 */
const AnalysisProgressPanel: React.FC<{ progress: AnalysisProgressState }> = ({ progress }) => {
    if (progress.stage === 'idle' || progress.stage === 'done') return null;

    const { stage, reportsLoaded, reportsTotal, analyzed, currentProvider, errorMessage } = progress;

    if (stage === 'error') {
        return (
            <div
                className="flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-900 dark:bg-red-950"
                role="alert"
            >
                <MdErrorOutline className="mt-0.5 shrink-0 text-red-600 dark:text-red-400" size={20} />
                <div>
                    <p className="text-sm font-semibold text-red-800 dark:text-red-200">
                        Não foi possível concluir a análise
                    </p>
                    {errorMessage && (
                        <p className="mt-1 text-sm text-red-700 dark:text-red-300">{errorMessage}</p>
                    )}
                </div>
            </div>
        );
    }

    const percentage =
        reportsTotal && reportsTotal > 0
            ? Math.min(100, Math.round((analyzed / reportsTotal) * 100))
            : null;

    return (
        <section
            className="rounded-lg border border-blue-200 bg-blue-50 p-4 dark:border-slate-700 dark:bg-slate-800"
            aria-live="polite"
            aria-busy="true"
        >
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                    Analisando o lote
                    {currentProvider && (
                        <span className="ml-2 rounded bg-white px-2 py-0.5 text-xs font-medium text-blue-700 dark:bg-slate-700 dark:text-blue-300">
                            {currentProvider}
                        </span>
                    )}
                </p>
                <p className="text-xs text-gray-600 dark:text-gray-400">
                    Você já pode consultar os documentos carregados abaixo
                </p>
            </div>

            <ul className="space-y-2">
                <StageRow
                    stage="loading-reports"
                    current={stage}
                    detail={
                        reportsTotal
                            ? `${reportsLoaded.toLocaleString('pt-BR')} / ${reportsTotal.toLocaleString('pt-BR')}`
                            : reportsLoaded > 0
                              ? reportsLoaded.toLocaleString('pt-BR')
                              : undefined
                    }
                />
                <StageRow
                    stage="applying-rules"
                    current={stage}
                    detail={
                        analyzed > 0
                            ? `${analyzed.toLocaleString('pt-BR')}${
                                  reportsTotal ? ` / ${reportsTotal.toLocaleString('pt-BR')}` : ''
                              }`
                            : undefined
                    }
                />
                <StageRow stage="consolidating" current={stage} />
            </ul>

            {percentage !== null && (
                <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-blue-100 dark:bg-slate-700">
                    <div
                        className="h-full rounded-full bg-blue-500 transition-all duration-300"
                        style={{ width: `${percentage}%` }}
                        role="progressbar"
                        aria-valuenow={percentage}
                        aria-valuemin={0}
                        aria-valuemax={100}
                        aria-label="Progresso da análise"
                    />
                </div>
            )}
        </section>
    );
};

export default AnalysisProgressPanel;
